// Чистые помощники управления временем для раздела «Спутники». Без MapLibre и
// React — только арифметика моментов и человекочитаемый вывод, чтобы всё это
// покрывалось юнит-тестами. Момент везде трактуем в UTC: позиции спутников —
// это пропагация TLE на инстант, часовой пояс оператора значения не имеет, а
// UTC делает вывод и шаги детерминированными (независимыми от машины теста).

export type TimeStep = '-month' | '-24h' | '-6h' | '-1h' | '+1h' | '+6h' | '+24h' | '+month'

const HOUR_MS = 3_600_000

/** Число дней в UTC-месяце (0-й день следующего месяца = последний день этого). */
function daysInUTCMonth(year: number, monthIndex0: number): number {
  return new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate()
}

/** Прибавляет delta месяцев в UTC с КЛЭМПОМ числа на последний день целевого
 *  месяца. Голый setUTCMonth переполняется: у 31 января +месяц нет «31 февраля»,
 *  и он утекал в 3 марта. Считаем целевой год/месяц явно и зажимаем день. */
function addUTCMonths(base: Date, delta: number): Date {
  const originalDay = base.getUTCDate()
  const monthAbs = base.getUTCMonth() + delta
  const targetYear = base.getUTCFullYear() + Math.floor(monthAbs / 12)
  const targetMonth = ((monthAbs % 12) + 12) % 12
  const clampedDay = Math.min(originalDay, daysInUTCMonth(targetYear, targetMonth))
  const d = new Date(base.getTime())
  // Год/месяц/день одним вызовом — промежуточного переполнения не возникает.
  d.setUTCFullYear(targetYear, targetMonth, clampedDay)
  return d
}

/** Смещает момент на шаг, возвращая НОВЫЙ Date (base не мутируется).
 *
 *  Часовые шаги — это ровно ±N·часов в миллисекундах. Месячные шаги — календарные
 *  (см. addUTCMonths), а не «30 суток»: −месяц от 15 марта → 15 февраля, а не «минус
 *  720 часов». UTC убирает неоднозначность перехода на летнее время (тот же
 *  результат в любом часовом поясе рантайма/теста). */
export function stepTime(base: Date, step: TimeStep): Date {
  const d = new Date(base.getTime())
  switch (step) {
    case '-1h':
      return new Date(d.getTime() - HOUR_MS)
    case '+1h':
      return new Date(d.getTime() + HOUR_MS)
    case '-6h':
      return new Date(d.getTime() - 6 * HOUR_MS)
    case '+6h':
      return new Date(d.getTime() + 6 * HOUR_MS)
    case '-24h':
      return new Date(d.getTime() - 24 * HOUR_MS)
    case '+24h':
      return new Date(d.getTime() + 24 * HOUR_MS)
    case '-month':
      return addUTCMonths(d, -1)
    case '+month':
      return addUTCMonths(d, 1)
  }
}

/** Момент → строка для API (ISO 8601, UTC). Бэкенд принимает её как time. */
export function toApiTime(d: Date): string {
  return d.toISOString()
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Человекочитаемый вывод момента: `YYYY-MM-DD HH:MM UTC`. Части берём в UTC —
 *  тот же инстант всегда даёт ту же строку (детерминизм для тестов и подписей). */
export function formatLocal(d: Date): string {
  const y = d.getUTCFullYear()
  const mo = pad2(d.getUTCMonth() + 1)
  const day = pad2(d.getUTCDate())
  const h = pad2(d.getUTCHours())
  const mi = pad2(d.getUTCMinutes())
  return `${y}-${mo}-${day} ${h}:${mi} UTC`
}
